import { useState, useEffect, type ChangeEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { HOME_ROUTE, LOGIN_ROUTE } from "../utils/const";
import { setCookie } from "../utils/cookies";
import { usePostAuthClientsMutation } from "../mutations/usePostAuthClientsMutation";
import { useConfirmPhoneMutation } from "../mutations/useConfirmPhoneMutation";
import { useConfirmPhoneCodeMutation } from "../mutations/useConfirmPhoneCodeMutation";
import { useSetPasswordMutation } from "../mutations/useSetPasswordMutation";
import { useGetClientQuery } from "@/queries/useGetClientQuery";
import { useGetClubsQuery } from "@/queries/useGetClubsQuery";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";

export const useAuthForm = () => {
  const { login: contextLogin } = useAuth();
  const { setSelectedClub } = useClub();
  const location = useLocation();
  const isLogin = location.pathname === LOGIN_ROUTE;
  const navigate = useNavigate();

  // Состояния для формы
  const [phone, setPhone] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [inputCode, setInputCode] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [passwordCheck, setPasswordCheck] = useState<string>("");
  const [inputPassword, setInputPassword] = useState<boolean>(false);
  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(false);
  const [userToken, setUserToken] = useState<string>("");

  const { mutateAsync: authClients } = usePostAuthClientsMutation();
  const { mutateAsync: confirmPhoneMutate } = useConfirmPhoneMutation();
  const { mutateAsync: confirmPhoneCodeMutate } = useConfirmPhoneCodeMutation();
  const { mutateAsync: setPasswordMutate } = useSetPasswordMutation();

  // Only fetch client and clubs after authentication
  const { data: clientData } = useGetClientQuery({ enabled: !!userToken });
  const { data: clubsData } = useGetClubsQuery({ enabled: !!userToken });

  // Состояния для таймера
  const [isTimerActive, setIsTimerActive] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(0);

  useEffect(() => {
    setPasswordsMatch(password === passwordCheck);
  }, [password, passwordCheck]);

  // Обработчики формы
  const setPhoneValue = (event: ChangeEvent<HTMLInputElement>) => {
    // Extract 10 user digits from masked value (skip +7 prefix)
    const allDigits = event.target.value.replace(/\D/g, "");
    const userDigits = allDigits.startsWith("7") ? allDigits.slice(1) : allDigits;
    setPhone(userDigits);
  };

  // Format phone for API: +7 + 10 digits
  const formatPhone = (digits: string) => (digits ? `+7${digits}` : "");

  const setCodeValue = (value: string) => {
    setCode(value);
  };

  const setPasswordValue = (value: string) => {
    setPassword(value);
  };

  const setPasswordValueCheck = (value: string) => {
    setPasswordCheck(value);
  };

  // Handle client and clubs data after authentication
  useEffect(() => {
    if (clientData && clubsData && userToken) {
      const userName = `${clientData.name} ${clientData.last_name}`;

      let clubName = clientData.club?.name;
      let idClub = clientData.club?.id;

      if (!clubName || !idClub) {
        clubName = clubsData[0]?.title;
        idClub = clubsData[0]?.id;
      }

      if (idClub) {
        setSelectedClub(idClub);
      }
      contextLogin(userToken, userName, formatPhone(phone));

      navigate(HOME_ROUTE);
    }
  }, [clientData, clubsData, userToken, phone, setSelectedClub, contextLogin, navigate]);

  // Обработчики API
  const confirmPhonePost = (digits: string) => {
    confirmPhoneMutate(formatPhone(digits));
  };

  const passwordPost = async (digits: string, password: string, token: string) => {
    await setPasswordMutate({ phone: formatPhone(digits), password, token });
  };

  const authPost = async (digits: string, password: string) => {
    const cleanPhone = formatPhone(digits);
    try {
      const authResponse = await authClients({ phone: cleanPhone, password });
      const token = authResponse.data.user_token;
      toast.success("Вы успешно авторизовались");
      // Store token temporarily; full login happens after client/clubs data loads
      setCookie("user_token", token);
      setUserToken(token); // This will trigger the queries
    } catch (err) {
      const error = err as AxiosError<{ error_message: string }>;
      let errorMessage: string;
      if (error.response) {
        switch (error.response.status) {
          case 500:
            errorMessage = "На сервере ведутся технические работы. Сервис временно недоступен";
            break;
          case 502:
          case 503:
          case 504:
            errorMessage = "Сервер временно недоступен. Попробуйте позже";
            break;
          case 401:
            errorMessage = "Ошибка авторизации. Проверьте номер телефона и пароль";
            break;
          case 403:
            errorMessage = "Доступ запрещён. Обратитесь к администратору";
            break;
          default:
            errorMessage = error.response.data?.error_message || "Произошла ошибка";
        }
      } else if (error.request) {
        errorMessage = "Ошибка сети. Проверьте подключение к интернету";
      } else {
        errorMessage = "Ошибка авторизации. Проверьте номер телефона и пароль";
      }
      toast.error(errorMessage);
    }
  };

  const handleBlockSms = () => {
    setIsTimerActive(true);
    setTimer(60);

    const timerInterval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          clearInterval(timerInterval);
          setIsTimerActive(false);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
  };

  const handleResendCode = () => {
    confirmPhonePost(phone);
    handleBlockSms();
  };

  const loginToggle = () => {
    setInputCode(false);
    setInputPassword(false);
  };

  const click = async () => {
    if (isLogin) {
      authPost(phone, password);
    } else {
      if (!inputCode && !inputPassword) {
        setInputCode(true);
        confirmPhonePost(phone);
        handleBlockSms();
      } else if (inputCode && !inputPassword) {
        confirmPhoneCodeMutate({ phone: formatPhone(phone), code })
          .then(() => {
            setInputCode(false);
            setInputPassword(true);
          })
          .catch(() => {
            toast.error("Неверный код из СМС");
          });
      } else if (!inputCode && inputPassword) {
        if (password === passwordCheck && password.length >= 4) {
          const token = localStorage.getItem("pass_token");
          await passwordPost(phone, password, token);
          setInputPassword(false);
          await authPost(phone, password);
        }
      }
    }
  };

  return {
    phone,
    code,
    inputCode,
    isLogin,
    password,
    passwordCheck,
    inputPassword,
    passwordsMatch,
    isTimerActive,
    timer,
    setPhoneValue,
    setCodeValue,
    setPasswordValue,
    setPasswordValueCheck,
    handleResendCode,
    loginToggle,
    click,
  };
};
